import React, { useState, useEffect } from 'react';
import { customerService } from '../../../../services/apiService';
import { 
  Users, 
  Activity, 
  CreditCard, 
  UserPlus, 
  AlertTriangle, 
  ShieldAlert, 
  Trophy,
  AlertCircle
} from 'lucide-react';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 đ';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
};

export default function Customer360Section({ merchant_id, start_date, end_date, trigger360 }) {
  const [data, setData] = useState({
    activeCustomers: 0,
    frequency: 0,
    arpc: 0,
    customerTypes: null,
    revenueConcentration: [],
    cohorts: [],
    rfm: [],
    churnPrediction: null,
    recency: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Chờ cho trigger360 = true (nghĩa là API Totals bên Customer.jsx đã chạy xong)
    if (!merchant_id || !start_date || !end_date || !trigger360) return;

    const fetchCustomerData = async () => {
      setLoading(true);
      const params = {
          merchant_id,
          start_date,
          end_date,
          report_date: end_date
      };

      try {
        const activeRes = await customerService.getActiveCustomers(params).catch(e => {
          console.warn('Lỗi API active-customers:', e.message);
          return null;
        });

        const freqRes = await customerService.getFrequency(params).catch(e => {
          console.warn('Lỗi API frequency:', e.message);
          return null;
        });

        const arpcRes = await customerService.getArpc(params).catch(e => {
          console.warn('Lỗi API arpc:', e.message);
          return null;
        });

        const typeRes = await customerService.getCustomerTypes(params).catch(e => {
          console.warn('Lỗi API customer-types:', e.message);
          return null;
        });

        const concRes = await customerService.getRevenueConcentration(params).catch(e => {
          console.warn('Lỗi API revenue-concentration:', e.message);
          return null;
        });

        const cohortsRes = await customerService.getCohorts(params).catch(e => {
          console.warn('Lỗi API cohorts:', e.message);
          return null;
        });

        const rfmRes = await customerService.getRfm({ ...params, days_back: 30 }).catch(e => {
          console.warn('Lỗi API rfm:', e.message);
          return null;
        });

        const churnRes = await customerService.getChurnPrediction({ ...params, recency_threshold: 30, frequency_decline_months: 2 }).catch(e => {
          console.warn('Lỗi API churn-prediction:', e.message);
          return null;
        });

        const recencyRes = await customerService.getRecency(params).catch(e => {
          console.warn('Lỗi API recency:', e.message);
          return null;
        });

        setData({
          activeCustomers: activeRes?.data?.active_customers ?? 0,
          frequency: freqRes?.data?.frequency ?? 0,
          arpc: arpcRes?.data?.arpc ?? 0,
          customerTypes: typeRes?.data ?? null,
          revenueConcentration: concRes?.data?.concentration_metrics ?? [],
          cohorts: cohortsRes?.data?.cohorts ?? [],
          rfm: rfmRes?.data?.customers ?? [],
          churnPrediction: churnRes?.data ?? null,
          recency: recencyRes?.data ?? []
        });
      } catch (error) {
        console.error("Error fetching customer 360 data sequentially:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [merchant_id, start_date, end_date, trigger360]);

  // Skeleton Loading Profile
  if (loading) {
    return (
      <div className="w-full space-y-6 animate-pulse p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl shadow-sm"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-80 bg-gray-200 rounded-xl shadow-sm"></div>
          <div className="h-80 bg-gray-200 rounded-xl shadow-sm"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-64 bg-gray-200 rounded-xl shadow-sm"></div>
          <div className="h-64 bg-gray-200 rounded-xl shadow-sm"></div>
        </div>
      </div>
    );
  }

  // Khúc tính toán RFM Segmentation
  const totalRfmCustomers = data.rfm.length || 1; 
  const rfmSegmentsMap = data.rfm.reduce((acc, curr) => {
    acc[curr.rfm_segment] = (acc[curr.rfm_segment] || 0) + 1;
    return acc;
  }, {});
  
  const rfmColors = {
    'Champions': 'bg-emerald-500',
    'Loyal Customers': 'bg-blue-500',
    'Potential Loyalist': 'bg-indigo-400',
    'Recent Customers': 'bg-teal-400',
    'Promising': 'bg-lime-400',
    'Customers Needing Attention': 'bg-yellow-400',
    'About To Sleep': 'bg-orange-400',
    'At Risk': 'bg-red-400',
    'Can\'t Lose Them': 'bg-rose-600',
    'Hibernating': 'bg-gray-400',
    'Lost': 'bg-slate-600',
  };

  const topChampions = data.rfm
    .filter(c => c.rfm_segment === 'Champions')
    .sort((a, b) => b.monetary - a.monetary)
    .slice(0, 5);

  // Khúc tính toán Cảnh báo Revenue Concentration
  const top10Concentration = data.revenueConcentration.find(t => t.tier === 'top_10_percent');
  const isHighRiskConcentration = top10Concentration && top10Concentration.revenue_percentage > 30;

  return (
    <div className="w-full space-y-6">
      
      {/* Phân khu 1: Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Tổng khách hoạt động */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Khách hoạt động</h3>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Users size={20} />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-800">{data.activeCustomers}</p>
          </div>
        </div>

        {/* Card 2: Khách Mới vs Khách Cũ */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500">Khách Mới / Cũ</h3>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
              <UserPlus size={20} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold text-emerald-600">{data.customerTypes?.new_customers || 0} Mới</span>
              <span className="font-semibold text-blue-600">{data.customerTypes?.returning_customers || 0} Cũ</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 flex overflow-hidden">
              <div 
                className="bg-emerald-500 h-2.5" 
                style={{ width: `${(data.customerTypes?.new_customer_ratio || 0) * 100}%` }}
              ></div>
              <div 
                className="bg-blue-500 h-2.5" 
                style={{ width: `${(data.customerTypes?.returning_customer_ratio || 0) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Card 3: Tần suất mua */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Tần suất mua TB</h3>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <Activity size={20} />
            </div>
          </div>
          <div className="mt-4 flex items-end">
            <p className="text-3xl font-bold text-gray-800">{data.frequency ? data.frequency.toFixed(1) : 0}</p>
            <span className="text-sm text-gray-400 ml-2 mb-1">lần/khách</span>
          </div>
        </div>

        {/* Card 4: ARPC */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Chi tiêu TB (ARPC)</h3>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <CreditCard size={20} />
            </div>
          </div>
          <div className="mt-4">
            <p className="text-3xl font-bold text-gray-800">{formatCurrency(data.arpc)}</p>
          </div>
        </div>
      </div>

      {/* Phân khu 2: Phân Tích Phân Khúc (RFM Analysis) */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Cột trái: Biểu đồ Tỷ trọng RFM */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Users size={20} className="mr-2 text-indigo-500" />
            Tỷ trọng Phân khúc Khách hàng (RFM)
          </h3>
          <div className="space-y-4">
            {Object.entries(rfmSegmentsMap)
              .sort(([, aCount], [, bCount]) => bCount - aCount)
              .map(([segment, count]) => {
              const percentage = ((count / totalRfmCustomers) * 100).toFixed(1);
              const bgColorClass = rfmColors[segment] || 'bg-gray-300';
              
              return (
                <div key={segment}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{segment}</span>
                    <span className="text-gray-500">{count} khách ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`${bgColorClass} h-2 rounded-full`} style={{ width: `${percentage}%` }}></div>
                  </div>
                </div>
              );
            })}
            {Object.keys(rfmSegmentsMap).length === 0 && (
              <p className="text-sm text-gray-500 italic text-center py-4">Chưa có dữ liệu phân khúc RFM</p>
            )}
          </div>
        </div>

        {/* Cột phải: Top 5 Champions */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <Trophy size={20} className="mr-2 text-yellow-500" />
            Vinh danh Top Khách hàng (Champions)
          </h3>
          <div className="flex-1 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 rounded-tl-lg">Khách hàng</th>
                  <th className="px-4 py-3 text-center">Giao dịch</th>
                  <th className="px-4 py-3 text-right rounded-tr-lg">Tổng chi tiêu</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topChampions.length > 0 ? (
                  topChampions.map((customer, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-800">{customer.customer_name}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{customer.frequency}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                        {formatCurrency(customer.monetary)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-4 py-8 text-center text-gray-500 italic">
                      Chưa có khách hàng phân khúc Champions
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Phân khu 3: Cảnh Báo Rủi Ro (Risk Management) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Thẻ Cảnh báo Đỏ (Churn Risk) */}
        <div className="bg-white rounded-xl shadow-sm border border-red-100 overflow-hidden">
          <div className="bg-red-50 p-4 border-b border-red-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ShieldAlert className="text-red-500" size={24} />
              <h3 className="text-lg font-semibold text-red-700">Nguy cơ rời bỏ (Churn Risk)</h3>
            </div>
            <span className="bg-red-100 text-red-700 py-1 px-3 rounded-full font-bold text-sm">
              {data.churnPrediction?.total_churn_risk_count || 0} Khách
            </span>
          </div>
          <div className="p-0">
            <div className="max-h-[300px] overflow-y-auto">
              {data.churnPrediction?.churn_risk_customers?.length > 0 ? (
                <table className="min-w-full text-left text-sm">
                  <thead className="sticky top-0 bg-white shadow-sm text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Tên Khách Hàng</th>
                      <th className="px-4 py-3 text-center">Số ngày bỏ lỡ</th>
                      <th className="px-4 py-3 text-center">Tần suất cũ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.churnPrediction.churn_risk_customers.map((c, idx) => (
                      <tr key={idx} className="hover:bg-red-50/50">
                        <td className="px-4 py-3 font-medium text-gray-700">{c.customer_name}</td>
                        <td className="px-4 py-3 text-center text-red-600 font-semibold">{c.recency_days} ngày</td>
                        <td className="px-4 py-3 text-center text-gray-500">{c.previous_frequency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-8 text-center text-gray-500 italic">
                  Không phát hiện khách hàng có rủi ro rời bỏ. Tuyệt vời!
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Thẻ Cảnh báo Cam (Revenue Concentration) */}
        <div className="bg-white rounded-xl shadow-sm border border-orange-100 overflow-hidden flex flex-col">
          <div className="bg-orange-50 p-4 border-b border-orange-100 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="text-orange-500" size={24} />
              <h3 className="text-lg font-semibold text-orange-700">Cảnh báo Phụ thuộc Doanh thu</h3>
            </div>
          </div>
          <div className="p-6 flex-1 flex flex-col">
            {isHighRiskConcentration && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex space-x-3 items-start">
                <AlertCircle className="text-red-500 mt-0.5 shrink-0" size={20} />
                <p className="text-sm text-red-800">
                  <strong className="block mb-1 text-red-900">Mức độ rủi ro cao!</strong>
                  Top 10% khách hàng đang chiếm <strong>{top10Concentration.revenue_percentage}%</strong> tổng doanh thu. 
                  Sự rời đi của nhóm khách này có thể tác động tiêu cực đến cửa hàng của bạn.
                </p>
              </div>
            )}
            
            <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">Chi tiết theo các mức Tier</h4>
            <div className="space-y-4">
              {data.revenueConcentration.length > 0 ? (
                data.revenueConcentration.map((tier, idx) => (
                  <div key={idx} className="flex flex-col space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700 capitalize">
                        {tier.tier.replace(/_/g, ' ')}
                      </span>
                      <span className="text-gray-900 font-semibold">{tier.revenue_percentage}% doanh thu</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{tier.customer_count} Khách hàng ({tier.customer_percentage}%)</span>
                      <span>{formatCurrency(tier.revenue_amount)}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                      <div 
                        className={`h-1.5 rounded-full ${tier.revenue_percentage > 30 ? 'bg-red-500' : 'bg-orange-400'}`} 
                        style={{ width: `${Math.min(tier.revenue_percentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 italic py-4">Chưa có dữ liệu phân bổ.</div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
